import { AgGridReact } from 'ag-grid-react';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { usersTableColumnsDefinitions, usersTableDefaultColumnsDefs } from '../models/columnDefinitions';
import { useIntl } from 'react-intl';
import { usePrevious } from '../../../hooks/usePrevious';
import {
    AgGridEvent,
    ClientSideRowModelModule,
    GridApi,
    ColumnResizedEvent,
    FilterChangedEvent,
    GridReadyEvent,
    SelectionChangedEvent,
} from 'ag-grid-community';
import { UsersModel } from '../../../../core/domainModels/users/UsersModel';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../../state/ApplicationState';
import {
    applyInitialSortFromDisplayPreferences,
    createTachoColumnState,
} from '../../../../core/domainModels/displayPreferences/tachoColumnState';
import { modifyUsersDisplayPreferences, modifyUsersTableFilters } from '../../../../state/users/reducer';
import AgGridContextMenuWrapper from '../../../components/ContextMenu/AgGridContextMenuWrapper';

interface UsersTableProps {
    users: Array<UsersModel>;
    onUserDoubleClicked: () => void;
    onSelectionChanged: (companies: Array<UsersModel>) => void;
}
export const UsersTable = (props: UsersTableProps) => {
    const { formatMessage } = useIntl();
    const dispatch = useDispatch();
    const gridApi = useRef<GridApi>();
    const displayPreferences = useSelector((state: ApplicationState) => state.users.displayPreferences);
    const tableFiltersState = useSelector((state: ApplicationState) => state.users.tableFilters);

    const columnDefs = useMemo(
        () => usersTableColumnsDefinitions(formatMessage, displayPreferences),
        [formatMessage, displayPreferences],
    );

    const previousUsers = usePrevious(props.users);
    useEffect(() => {
        const added = props.users.filter(activity => !previousUsers?.find(prevActivity => prevActivity.id === activity.id));
        const removed = previousUsers?.filter(prevActivity => !props.users?.find(activity => prevActivity.id === activity.id));
        const updated = props.users.filter(activity => !added.some(addedActivity => addedActivity.id === activity.id));
        gridApi.current?.applyTransaction({
            add: added,
            remove: removed,
            update: updated,
        });
    }, [props.users]);

    const rowsSelectionChanged = (event: SelectionChangedEvent) => {
        const selectedNodes = event.api.getSelectedNodes()?.map(node => node.data as UsersModel);
        if (!selectedNodes) {
            console.log('No selected rows.');
            return;
        }

        props.onSelectionChanged(selectedNodes);
    };

    const onGridReady = useCallback(
        (params: GridReadyEvent) => {
            gridApi.current = params.api;
            applyInitialSortFromDisplayPreferences(params, displayPreferences, [{ colId: 'email', sort: 'asc' }]);
            params.api.setFilterModel(tableFiltersState);
            params.api.setGridOption('rowData', props.users);
        },
        [props.users, displayPreferences, tableFiltersState],
    );
    function onColumnsChanged(event: AgGridEvent): void {
        const tachoColumnState = createTachoColumnState(event.api.getColumnState());
        dispatch(modifyUsersDisplayPreferences(tachoColumnState));
    }
    const onColumnResized = (event: ColumnResizedEvent) => {
        if (event.finished && event.source === 'uiColumnResized') {
            return onColumnsChanged(event);
        }
    };
    const onTableFiltersStateChanged = (event: FilterChangedEvent) => {
        const filters = event.api.getFilterModel();
        dispatch(modifyUsersTableFilters(filters));
    };
    return (
        <AgGridContextMenuWrapper gridApi={gridApi.current} showStatusBar>
            <AgGridReact
                suppressPropertyNamesCheck
                getRowId={params => params.data?.id ?? ''}
                suppressDragLeaveHidesColumns
                suppressContextMenu={true}
                multiSortKey="ctrl"
                rowSelection="multiple"
                defaultColDef={usersTableDefaultColumnsDefs}
                columnDefs={columnDefs}
                modules={[ClientSideRowModelModule]}
                onGridReady={onGridReady}
                onSelectionChanged={rowsSelectionChanged}
                suppressScrollOnNewData={true}
                onRowDoubleClicked={params => props.onUserDoubleClicked()}
                onSortChanged={event => onColumnsChanged(event)}
                onColumnMoved={event => onColumnsChanged(event)}
                onColumnResized={event => onColumnResized(event)}
                onFilterChanged={event => onTableFiltersStateChanged(event)}
            />
        </AgGridContextMenuWrapper>
    );
};
